// One item's whole life, drawn as a timeline.
//
// The account already knows all of this — who handed the item over and when, when
// it showed up in the inventory, when it left, what it sold for — spread across
// the marketplace ledger, the Steam trade log and the inventory sync diffs.
// Nothing put it together, so the answer to "where did this come from?" was a
// manual search through three tabs. The assembly is server-side
// (services/itemHistory.js); this only draws what comes back.
//
// CERTAIN AND POSSIBLE ARE SHOWN APART
// A float + paint seed match is proof: it is that exact item and no other copy.
// Steam's trade page often gives a bare name with no float, and those records
// can only be matched on name and wear — which cannot tell three identical
// Redlines apart. Those are drawn below the timeline, dimmed and folded away,
// labelled as possible. Mixing them into the proven line would make a guess look
// like a fact, which is the one thing this must not do.

import React, { useEffect, useState } from 'react';
import { apiFetch, getUser } from '../api';
import './ItemHistoryTimeline.css';

// Label, dot colour and the money sign each kind of event carries.
// `flow` decides whether a price is drawn as money in (+) or money out (-);
// null means the event moved no money at all.
const KINDS = {
  purchase:      { label: 'Bought',            color: '#f87171', flow: 'out'  },
  received:      { label: 'Received',          color: '#818cf8', flow: null   },
  appeared:      { label: 'Seen in inventory', color: '#64748b', flow: null   },
  tracked:       { label: 'Tracked',           color: '#38bdf8', flow: 'out'  },
  held:          { label: 'In your inventory', color: '#4ade80', flow: null   },
  stored:        { label: 'Storage unit',      color: '#a78bfa', flow: null   },
  sent:          { label: 'Sent',              color: '#fbbf24', flow: null   },
  disappeared:   { label: 'Left inventory',    color: '#94a3b8', flow: null   },
  'row-traded':  { label: 'Traded away',       color: '#fbbf24', flow: null   },
  sale:          { label: 'Sold',              color: '#4ade80', flow: 'in'   },
  'row-sold':    { label: 'Marked sold',       color: '#4ade80', flow: 'in'   },
};

// Steam's own word for the shape of a handover. TRADE means items crossed both
// ways; the other is a one-way send, which is what a marketplace sale looks like
// from the inventory's side.
const DIRECTION_NOTE = {
  TRADE:                    'swap',
  LIKELY_SALE_OR_TRANSFER:  'one-way',
  RECEIVED:                 null,
};

function fmtDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function money(n) {
  return `$${Number(n || 0).toFixed(2)}`;
}

/**
 * Why the request failed, in the caller's terms.
 *
 * A bare "could not load" sent me looking for a data problem when the answer was
 * that the API being talked to did not have the route yet — the website can be
 * pointed at the production API while the endpoint exists only locally
 * (.env.development.local), and every item then fails identically. The status
 * code is the whole diagnosis, so it is shown.
 */
function reasonFor(status) {
  if (status === '404') return 'This API does not have item history yet (404) — the server needs the update deployed, or point the site at your local one.';
  if (status === '401' || status === '403') return 'Your session expired. Sign in again.';
  if (status === '503') return 'The account is busy on another process. Try again in a moment.';
  return `Could not load this item's history${status ? ` (${status})` : ''}`;
}

function Party({ party }) {
  if (!party) return null;
  if (!party.profileUrl) return <span className="ihist-party">{party.name}</span>;
  return (
    <a
      className="ihist-party link"
      href={party.profileUrl}
      target="_blank"
      rel="noreferrer"
      onClick={e => e.stopPropagation()}
    >
      {party.name}
    </a>
  );
}

function Event({ event }) {
  const kind = KINDS[event.kind] || { label: event.kind, color: '#94a3b8', flow: null };
  const date = fmtDate(event.at);
  const time = fmtTime(event.at);
  const note = DIRECTION_NOTE[event.detail] ?? (event.detail || null);
  // A cancelled or failed marketplace trade is kept in the line, marked: it is
  // part of what happened, and reading it as an ending would be wrong.
  const dead = event.settled === false;

  return (
    <li className={`ihist-ev${dead ? ' dead' : ''}${event.certain ? '' : ' maybe'}`}>
      <span className="ihist-dot" style={{ background: kind.color }} aria-hidden="true" />
      <span className="ihist-when">
        {date || 'no date'}
        {time && <span className="ihist-time">{time}</span>}
      </span>
      <span className="ihist-what">
        <span className="ihist-kind">{kind.label}</span>
        {event.party && <> {event.kind === 'sent' || event.kind === 'sale' || event.kind === 'row-traded' ? 'to' : 'from'} <Party party={event.party} /></>}
        {event.market && <span className="ihist-tag">{event.market}</span>}
        {note && <span className="ihist-tag soft">{note}</span>}
        {dead && <span className="ihist-tag bad">{event.state}</span>}
      </span>
      {event.price != null && kind.flow && (
        <span className={`ihist-money ${kind.flow}`}>
          {kind.flow === 'in' ? '+' : '-'}{money(event.price)}
        </span>
      )}
    </li>
  );
}

/**
 * @param {object} props.item  what to follow: { name, condition, float, paintSeed, assetId }.
 *   float + paintSeed are what make the answer exact — pass them whenever the
 *   caller has them, even if it also has an assetId, since an item's assetId
 *   changes every time it moves and the fingerprint never does.
 */
export default function ItemHistoryTimeline({ item, variant = 'classic' }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    const user = getUser();
    if (!user) { setState({ loading: false, error: 'Not signed in', data: null }); return; }

    let live = true;
    const params = new URLSearchParams();
    if (item?.name)      params.set('name', item.name);
    if (item?.condition) params.set('condition', item.condition);
    if (item?.float != null)     params.set('float', String(item.float));
    if (item?.paintSeed != null) params.set('paintSeed', String(item.paintSeed));
    if (item?.assetId)   params.set('assetId', String(item.assetId));

    setState({ loading: true, error: null, data: null });
    apiFetch(`/api/tracker/item-history/${encodeURIComponent(user)}?${params}`)
      .then(res => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
      .then(data => { if (live) setState({ loading: false, error: null, data }); })
      .catch(err => { if (live) setState({ loading: false, error: reasonFor(err.message), data: null }); });

    return () => { live = false; };
  }, [item?.name, item?.condition, item?.float, item?.paintSeed, item?.assetId]);

  const [showMaybe, setShowMaybe] = useState(false);

  if (state.loading) return <div className={`ihist ${variant} ihist-note`}>Reading history…</div>;
  if (state.error)   return <div className={`ihist ${variant} ihist-note`}>{state.error}</div>;

  const events  = state.data?.events || [];
  const certain = events.filter(e => e.certain);
  const maybe   = events.filter(e => !e.certain);
  const ids     = state.data?.identity?.assetIds || [];

  return (
    <div className={`ihist ${variant}`}>
      {certain.length === 0 ? (
        <div className="ihist-note">
          Nothing recorded for this exact item.
          {maybe.length > 0 && ' Only name matches, below.'}
        </div>
      ) : (
        <ul className="ihist-list">
          {certain.map((e, i) => <Event key={`c${i}`} event={e} />)}
        </ul>
      )}

      {maybe.length > 0 && (
        <div className="ihist-maybe">
          <button type="button" className="ihist-toggle" onClick={() => setShowMaybe(v => !v)}>
            {showMaybe ? 'Hide' : 'Show'} {maybe.length} possible match{maybe.length !== 1 ? 'es' : ''}
          </button>
          {showMaybe && (
            <>
              <div className="ihist-note small">
                Matched on name and wear only — Steam did not record a float for these,
                so they could be another copy of the same skin.
              </div>
              <ul className="ihist-list">
                {maybe.map((e, i) => <Event key={`m${i}`} event={e} />)}
              </ul>
            </>
          )}
        </div>
      )}

      {ids.length > 0 && (
        <div className="ihist-ids">
          Asset {ids.length !== 1 ? 'ids' : 'id'}: {ids.join(', ')}
          {ids.length > 1 && <span className="ihist-note small"> — an item gets a new one every time it moves.</span>}
        </div>
      )}
    </div>
  );
}
